import catalogRequests from '@/requests/app/catalog';
import bookingRequests from '@/requests/app/booking';
import bannersRequests from '@/requests/app/banners';
import { ProfileState } from './index';
import { RootState } from '@/store';
import { ActionTree } from 'vuex';
import {
  BookingV1BookingCreateRequest,
  AppealBooking,
  BookingV1DealCreateRequest,
  BookingV1DealPartialUpdateRequest,
  Deal,
  CatalogV1ItemMyListRequest,
} from '@/requests/mainApi';
import leadRequests from '@/requests/app/lead';

import { IFlatItem } from '@/views/App/Profile/store/interfaces';
import i18n from '@/lib/i18n';
import { LeadV1LeadListRequest } from '@/requests/mainApi/apis/LeadApi';

export const actions: ActionTree<ProfileState, RootState> = {
  async getBookingFlats({ commit }): Promise<void> {
    try {
      const bookingResponse = await bookingRequests.getBookingFlat({});

      if (!bookingResponse.body?.results.length) {
        return;
      }

      const flatsResponse = await catalogRequests.getFlatsInfo({
        id: bookingResponse.body?.results.map((item: AppealBooking) => item.estate),
      });

      let foundItem;
      commit(
        'setBookingFlats',
        flatsResponse.body!.results.map(flat => {
          foundItem = bookingResponse.body!.results.find(bookingFlat => bookingFlat.estate === flat.id);
          return {
            ...foundItem,
            ...flat,
            bookingStatus: foundItem?.status,
            bookingId: foundItem?.id,
          };
        })
      );
    } catch (e) {
      console.error(e);
    }
  },
  async getDeal({ commit }): Promise<void> {
    try {
      const response = await bookingRequests.getDeal();
      commit('setDeal', response.body, { root: true });
    } catch (e) {
      console.error(e);
    }
  },
  async updateDeal({ commit }, payload: BookingV1DealPartialUpdateRequest) {
    try {
      const response = await bookingRequests.updateDeal(payload);
      commit('setDeal', response.body, { root: true });
    } catch (e) {
      console.error(e);
    }
  },
  async updateBookingFlat({ commit, getters, rootGetters }, payload: BookingV1BookingCreateRequest): Promise<boolean> {
    try {
      const bookingResponse = await bookingRequests.updateBookingFlat(payload);
      const flatsResponse = await catalogRequests.getFlatsInfo({
        id: [bookingResponse.body!.estate],
      });

      commit('setBookingFlats', [
        ...getters.bookingFlats,
        {
          ...bookingResponse.body,
          ...flatsResponse.body!.results[0],
          bookingStatus: bookingResponse.body!.status,
          estate: bookingResponse.body!.estate,
          expiredAt: bookingResponse.body!.expiredAt,
        },
      ]);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  },
  dropBookingFlat({ commit, getters }, payload: number) {
    commit(
      'setBookingFlats',
      getters.bookingFlats.filter((flat: IFlatItem) => flat.estate !== payload)
    );
  },
  async getMyFlats({ commit }, payload: CatalogV1ItemMyListRequest): Promise<void> {
    commit('setLoadingFlats', true);

    try {
      const response = await catalogRequests.getMyFlats(payload);
      commit('setMyFlats', response.body);
    } catch (e) {
      console.error(e);
    } finally {
      commit('setLoadingFlats', false);
    }
  },
  async getMyFinishedFlats({ commit }, payload: CatalogV1ItemMyListRequest): Promise<void> {
    commit('setLoadingFinishedFlats', true);

    try {
      const response = await catalogRequests.getMyFlats(payload);
      commit('setMyFinishedFlats', response.body);
    } catch (e) {
      console.error(e);
    } finally {
      commit('setLoadingFinishedFlats', false);
    }
  },
  async getActions({ commit }): Promise<void> {
    try {
      const response = await bannersRequests.getActions({});
      commit('setActions', response.body!.results);
    } catch (e) {
      console.error(e);
    }
  },
  async getActionMain({ commit }): Promise<void> {
    try {
      const response = await bannersRequests.getActionMain();
      commit('setActionMain', response.body);
    } catch (e) {
      console.error(e);
    }
  },
  async startDeal({ commit, dispatch }, payload: BookingV1DealCreateRequest): Promise<boolean> {
    try {
      const response = await bookingRequests.createDeal(payload);
      if (response.status) {
        commit('setDeal', response.body, { root: true });
      } else {
        dispatch(
          'ui/addNotification',
          {
            message: (i18n.messages.ru.app as any).profile.flatsList.dealAlreadyExist,
          },
          { root: true }
        );
      }
      return response.status;
    } catch (e) {
      console.error(e);
      return false;
    }
  },
  async tryFinishDeal({ commit }): Promise<Deal | undefined> {
    try {
      const response = await bookingRequests.tryFinishDeal();
      return response.body;
    } catch (e) {
      console.error(e);
    }
  },

  async getLeads({ commit }, payload) {
    commit('setLoadingDeals', true);

    try {
      const response = await leadRequests.getLeads(payload);
      const next = response.body.next ? getNextOrPrev(response.body.next) : { limit: 5, offset: 0 };
      const prev = response.body.previous ? getNextOrPrev(response.body.previous) : { limit: 5, offset: 5 };

      return { next, prev, count: response.body.count, results: response.body.results };
    } catch (error) {
      console.error(error);
      return {};
    } finally {
      commit('setLoadingDeals', false);
    }
  },

  async approveLead({ commit }, payload) {
    try {
      await leadRequests.approveLead(payload);
    } catch (error) {
      console.error(error);
    }
  },
};

const getNextOrPrev = (str: string) => {
  return Object.fromEntries(
    str
      .split('?')[1]
      .split('&')
      .map((key: string) => key.split('='))
  ) as LeadV1LeadListRequest;
};
